import {
    type TeamsSubscribeAction,
    type TGetOrigin,
    type TSubscribeActionDescriptor,
    type TTeamsSendActionsDescriptor,
    Bridge,
    TeamsSendAction,
} from '@instant-messengers/vk-teams-bridge';
import { parse } from 'qs';
import { APIConfig } from 'reactApp/api/APIConfig';
import { enableWorkspaceSuperappMode } from 'reactApp/appHelpers/featuresHelpers/features/workspaceSuperapp';
import { TokenService } from 'reactApp/services/TokenService';

export type BridgeType = Bridge<
    TeamsSubscribeAction,
    Record<TeamsSubscribeAction, TSubscribeActionDescriptor>,
    TeamsSendAction,
    TTeamsSendActionsDescriptor
>;

type InitReturnType = {
    isError: boolean;
    isEnabled: boolean;
};

export class WorkspaceSuperApp {
    isEnabled = false;
    superAppOrigin: TGetOrigin['output'] = { origin: '', href: '' };
    private bridge: BridgeType;
    private tokenService: TokenService;

    constructor() {
        this.bridge = new Bridge();
        this.tokenService = new TokenService(this.bridge);
    }

    async init(): Promise<InitReturnType> {
        this.isEnabled = (this.checkIsSuperApp() || this.getIsSuperApp()) && enableWorkspaceSuperappMode;

        if (!this.isEnabled) {
            return { isError: false, isEnabled: this.isEnabled };
        }

        const token = await this.tokenService.init();

        if (token) {
            APIConfig.set('workspace-superapp-access-token', token);
        }

        this.superAppOrigin = await this.bridge.send(TeamsSendAction.GetOrigin, null);

        await this.bridge.send(TeamsSendAction.LoadingCompleted, {
            ok: true,
        });

        return { isError: !token, isEnabled: this.isEnabled };
    }

    private checkIsSuperApp() {
        const url = new URL(window.location.href);

        return url.searchParams.has('superapp');
    }

    private getIsSuperApp() {
        // по договоренности https://webim.internal.myteam.mail.ru/message/v1/QW9Lb3NBV0t3M1ItajlzX2ZRLzc1Nzc3MjA2MzEyMzY0OTQ0MDY=
        const url = new URL(window.location.href);

        return url.searchParams.get('source') === 'ws-superapp';
    }

    getAuthToken() {
        return this.tokenService.getAuthToken();
    }

    getAuthHeaderValue() {
        return `Bearer ${this.getAuthToken()}`;
    }

    sendAuthChangeEvent(path: string, params: string) {
        if (!this.isEnabled) {
            return;
        }

        this.bridge.send(TeamsSendAction.NavigationChanged, {
            path,
            params: parse(params, { ignoreQueryPrefix: true }),
        });
    }

    refreshToken() {
        return this.tokenService.fetchAccessToken();
    }

    openLink(url: string) {
        return this.bridge.send(TeamsSendAction.OpenLink, { url });
    }
}

let instance: WorkspaceSuperApp | null = null;

export const getWorkspaceSuperAppService = () => {
    if (typeof window === 'undefined') {
        return;
    }

    if (!instance) {
        instance = new WorkspaceSuperApp();
    }
    return instance;
};
